import { cx } from "@emotion/css";
import { Avatar, Box, Grid, IconButton, Typography } from "@mui/material";
import { DRAWER_INFINITE_SCROLL_PAGE_SIZE, KEYBOARD_ARROW_DOWN, SEARCH_DELAY_DEBOUNCE } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppImages } from "app/appImages";
import { GenericAbortSignal } from "axios";
import { MprcRequestErrorReport, MprcSpinnerIndicator, MprcTextMuted } from "components";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonClearSearch } from "components/iconButtons/IconButtonClearSearch";
import { IconUserGear } from "components/icons";
import { TextFieldFilter } from "components/MprcAdvFilter";
import MprcAutoSizer from "components/MprcAutoSizer";
import { MprcBoxDrawer } from "components/MprcBoxDrawer";
import { UserPanelDto, UserPanelDtoPaginatedList } from "generated/CoreApi_v1";
import { isCanceledError } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

////////////////////////////////////////////////////////////////////////////////

export type ListUsersFilter = {
    searchText: string;
    pageNumber: number;
    pageSize: number;
};


type ItemData = {
    items: UserPanelDto[];
};

////////////////////////////////////////////////////////////////////////////////


/**
 * Renderiza uma linha da Lista.
 * @param props
 */
function RenderRow(props: ListChildComponentProps<ItemData>) {
    const { index, style } = props;
    const { items } = props.data;

    if (index > items.length - 1) {
        return (
            <Box style={style} sx={{ mt: 1 }}>
                <MprcSpinnerIndicator />
            </Box>
        );
    }

    const item = items[index];
    return (
        <div key={index} style={style}>
            <Grid container alignItems="center" spacing={0.5} >
                <Grid>
                    <IconButton disableRipple disableFocusRipple>
                        <Avatar
                            src={item.thumbnailUrl ?? AppImages.UserThumbnail}
                            title={item.name}
                            sx={(theme) => ({ width: 32, height: 32, backgroundColor: theme.palette.grey[200] })}
                        />
                    </IconButton>
                </Grid>
                <Grid>
                    <MprcTextMuted>
                        <Typography variant="body2" component="div" noWrap={true}>
                            {item.name}
                        </Typography>
                    </MprcTextMuted>
                </Grid>
                {
                    item.isManager &&
                    <Grid>
                        <IconUserGear size="sm" color="muted" />
                    </Grid>
                }
            </Grid>
        </div>
    );
}

const MemoizedRenderRow = React.memo(RenderRow);

////////////////////////////////////////////////////////////////////////////////

export type RoleSelectorDrawerResult = {
    roleId: string;
    name: string;
};

interface IPros {
    title?: string;
    subtitle?: string;
    onGetData: (filter: ListUsersFilter, signal: GenericAbortSignal | undefined) => Promise<UserPanelDtoPaginatedList>;
    onClose: () => void;
}

export const ListUsersMostPublicationDrawer: React.FC<IPros> = (props) => {

    const { title, subtitle, onClose, onGetData } = props;
    const { t } = useTranslation();

    const [error, setError] = useState<any>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [items, setItems] = useState<UserPanelDto[]>([]);

    const currentPage = useRef<number>(1);
    const endOfRecords = useRef<boolean>(true);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);
    const hasMountedRef = useRef(false);

    let searchTextRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController>(undefined);

    ///
    async function loadData() {
        try {

            abortControllerRef.current = new AbortController();

            var result = await onGetData({
                searchText: searchText,
                pageNumber: currentPage.current,
                pageSize: DRAWER_INFINITE_SCROLL_PAGE_SIZE
            }, abortControllerRef.current.signal);

            if (currentPage.current == 1) {
                // Reseta o cache.
                if (hasMountedRef.current) {
                    if (infiniteLoaderRef.current) {
                        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
                    }
                }
                // Inicializa o array de itens.
                setItems(result.items);
            }
            else {
                // Adiciona mais itens no array.
                setItems([...items, ...result.items]);
            }

            // Há mais registros?
            endOfRecords.current = result.items.length < DRAWER_INFINITE_SCROLL_PAGE_SIZE;

            setTimeout(() => searchTextRef.current?.focus(), 100);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setError(e);
                endOfRecords.current = true;
            }
        }
        finally {
            setLoading(false);
            abortControllerRef.current = undefined;
        }
    }

    ///
    const loadNextPage = (startIndex: number, stopIndex: number) => {
        currentPage.current++;
        return new Promise<void>((resolve) => {
            loadData().then(() => resolve());
        });
    };

    ///
    const resetSearch = (ignoreHistory: boolean = false) => {
        currentPage.current = 1;
        setLoading(true);
        loadData();
        hasMountedRef.current = true;
    };

    // Se há mais itens para ser carregado, adicionamos UMA LINHA para que o Indicador de Carregamento apareça.
    const itemCount = !endOfRecords.current ? items.length + 1 : items.length;

    // Permite apenas uma página de items pode vez.
    const loadMoreItems = loading ? () => { } : loadNextPage;

    // Verifica se a linha está carregada, exceto o Indicador de Carregamento.
    const isItemLoaded = (index: number) => endOfRecords.current || index < items.length;

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////


    /// Executa a pesquisa depois de 600ms.
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            abortControllerRef.current?.abort();
            resetSearch();
        }, SEARCH_DELAY_DEBOUNCE);

        return () => {
            clearTimeout(delayDebounce);
            abortControllerRef.current?.abort();
        };
    }, [searchText]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcBoxDrawer
            onClose={() => onClose()}
            title={title || t("User.ListUsersDrawer.Title")}
            subtitle={subtitle}
            header={
                <TextFieldFilter
                    autoFocus
                    fullWidth
                    autoComplete="off"
                    placeholder={t("User.ListUsersDrawer.SearchTextPlaceholder")}
                    value={searchText}
                    onChange={() => {
                        setSearchText(searchTextRef.current?.value ?? "");
                    }}
                    onKeyDown={(e) => {
                        if (e.keyCode == 13) resetSearch();
                        else if (e.keyCode == KEYBOARD_ARROW_DOWN && searchTextRef.current?.value.length == 0) resetSearch(true);
                    }}
                    inputRef={searchTextRef}
                    slotProps={{
                        input: {
                            endAdornment:
                                searchText != "" &&
                                <IconButtonClearSearch
                                    size="small"
                                    onClick={() => setSearchText("")}
                                />
                        }
                    }}
                />
            }
            content={
                <>
                    {
                        error
                            ? <MprcRequestErrorReport error={error} />
                            : loading
                                ? <MprcSpinnerIndicator />
                                : !loading && itemCount == 0
                                    ? <MprcFilterEmptyDataset message={t("User.ListUsersDrawer.NoRecordsFoundMessage")} />
                                    : <MprcAutoSizer>
                                        {
                                            ({ width, height }) =>
                                                <InfiniteLoader
                                                    isItemLoaded={isItemLoaded}
                                                    itemCount={itemCount}
                                                    loadMoreItems={loadMoreItems}
                                                    ref={infiniteLoaderRef}
                                                    threshold={10}
                                                    minimumBatchSize={10}
                                                >
                                                    {({ onItemsRendered, ref }) => (
                                                        <FixedSizeList
                                                            height={height}
                                                            width={width}
                                                            itemData={{ items: items }}
                                                            itemCount={itemCount}
                                                            itemSize={40}
                                                            onItemsRendered={onItemsRendered}
                                                            ref={ref}
                                                            className={cx(AppGlobalClassKeys.drawerFixedSizeList, AppGlobalClassKeys.thinScrollbar)}
                                                        >
                                                            {MemoizedRenderRow}
                                                        </FixedSizeList>
                                                    )}
                                                </InfiniteLoader>
                                        }
                                    </MprcAutoSizer>
                    }
                </>
            }
        />
    );
};
